// src/ConversationList.js
import React, { useMemo } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { findByLabel } from './ConfigHolder';
import SearchToggleInput from './components/SearchToggleInput';
import useDualSearchInputs from './hooks/useDualSearchInputs';
import {Sentence} from './models/Sentence';
import { getBackgroundColor } from './resultUtils';
import { round } from 'lodash';

const getScoreColor = (score) => {
  if (score == null) return '#f5f5f5';
  if (score < 40) return '#ffcccc';
  if (score < 70) return '#fff3cd';
  return '#d4edda';
};


const ConversationList = ({
  content,
  onSelect,
  sentenceById = {},
  learnLabel,
  baseLabel,
  searchState,
  onSearchStateChange,
  selectedConversationId,
}) => {
  const {
    debouncedSearchTerm,
    inputRefs,
    searchTerm,
    showSearchInput,
    toggleSearchInput,
    updateSearchTerm,
    clearSearchTerm,
  } = useDualSearchInputs({
    controlledState: searchState,
    onStateChange: onSearchStateChange,
  });

  const learnCode = findByLabel(learnLabel);
  const baseCode = findByLabel(baseLabel);
  const keys = [learnCode?.flag || learnLabel || 'Learn', baseCode?.flag || baseLabel || 'Base'];
  const conversations = content?.conversations;

  const searchableConversations = useMemo(
    () => conversations.map((conv) => {
      const conversationTags = Array.isArray(conv.tags) ? conv.tags : [];

      const phraseTexts = [];
      const phraseTranslations = [];
      const phraseTags = [];

      (conv.sentences || []).forEach((line) => {
        const sentence = sentenceById[line?.phraseId];
        if (!sentence) return;
        if (sentence.sentence) phraseTexts.push(sentence.sentence);
        if (sentence.translation) phraseTranslations.push(sentence.translation);
        if (Array.isArray(sentence.tags)) phraseTags.push(...sentence.tags);
      });

      return {
        conv,
        searchLearn: [
          conv.title || '',
          conv.Level || '',
          ...conversationTags,
          ...phraseTexts,
          ...phraseTags,
        ].join(' ').toLowerCase(),
        searchBase: [
          conv.translation || '',
          ...conversationTags,
          ...phraseTranslations,
          ...phraseTags,
        ].join(' ').toLowerCase(),
      };
    }),
    [conversations, sentenceById]
  );

  const convScore = (conv) => {
    let avgScore = 0;
    for (const sentConv of conv.sentences) {
      const sent = sentenceById[sentConv.phraseId];
      avgScore += Sentence.avScore(sent.phraseObj);
    }
    avgScore = round(avgScore*100);  
    return avgScore;
  }

  const sentenceColors = (conv) => {
    const colors = [];
    for (const sentConv of conv.sentences) {
      const sentence = sentenceById[sentConv.phraseId];
      const color = getBackgroundColor(sentence);
      colors.push(color)
    }
    return colors;
  }

  const ConversationProgressBar = ({ colors, width = 60, height = 20 }) => {
    if (!colors || colors.length === 0) return null;

    const segmentWidth = width / colors.length;

    return (
      <div style={{ display: "flex", width, height, marginLeft: 8, gap: '3px' }}>
        {colors.map((c, i) => (
          <div
            key={i}
            style={{
              width: segmentWidth,
              height: "100%",
              backgroundColor: c, // your existing function
            }}
          />
        ))}
      </div>
    );
  };

  const filteredConversations = useMemo(() => {
    const search0 = (debouncedSearchTerm[0] || '').toLowerCase();
    const search1 = (debouncedSearchTerm[1] || '').toLowerCase();

    return searchableConversations
      .filter(({ searchLearn, searchBase }) => (
        searchLearn.includes(search0) && searchBase.includes(search1)
      ))
      .sort((a, b) => {
        const levelCompare = (a.conv.Level || '').localeCompare(b.conv.Level || '');
        if (levelCompare !== 0) return levelCompare;
        return (a.conv.title || '').localeCompare(b.conv.title || '');
      });
  }, [searchableConversations, debouncedSearchTerm]);

  return (
    <Paper sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Conversations
      </Typography>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        {keys.map((key, index) => (
          <Box
            key={index}
          >
            <SearchToggleInput
              active={!!(searchTerm[index] || showSearchInput[index])}
              clearAriaLabel={`Clear conversation search ${index}`}
              iconButtonSx={{ p: 1 }}
              inputRef={(el) => {
                inputRefs.current[index] = el;
              }}
              onChange={(value) => updateSearchTerm(index, value)}
              onClear={() => clearSearchTerm(index)}
              onToggle={(e) => {
                e.stopPropagation();
                toggleSearchInput(index);
              }}
              showInput={showSearchInput[index]}
              toolbarSx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              value={searchTerm[index]}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  flexGrow: 1,
                  minWidth: 90,
                }}
              >
                {key}
              </Typography>
            </SearchToggleInput>
          </Box>
        ))}
      </Box>

      <List dense>
        {filteredConversations.map(({ conv }) => {
          const validScores = conv.history
            ?.map((h) => h.average_score)
            .filter((s) => s != null);
          const avgScore = validScores?.length
            ? validScores.reduce((a, b) => a + b, 0) / validScores.length
            : null;
          

          const isSelected = conv._id === selectedConversationId;

          return (
            <ListItemButton
              key={conv._id}
              selected={isSelected}
              onClick={() => onSelect(conv)}
              sx={(theme) => ({
                mb: 0.5,
                borderRadius: 1,
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  boxShadow: `inset 4px 0 0 ${theme.palette.primary.main}`,
                },
                '&.Mui-selected:hover': {
                  backgroundColor: 'action.hover',
                },
              })}
            >
              <ListItemText
                primary={(
                  <>
                    <Typography component="span" variant="body1" fontWeight="bold">
                      {conv.title}
                    </Typography>
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      {conv.translation}
                    </Typography>
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                      ({conv.Level})
                    </Typography>
                  </>

                )}               
              />
              <ConversationProgressBar colors = {sentenceColors(conv)} />
            </ListItemButton>
          );
        })}
      </List>
    </Paper>
  );
};

export default ConversationList;
